import React, { useMemo } from 'react';
import {
  AmhsInstancedBatch,
  amhsCastShadow,
  amhsReceiveShadow,
  type AmhsDetailLevel,
} from '../rendering';
import { getStockerMaterials } from './StockerMaterials';
import { buildStockerTrayInstances } from './stockerGeometry';
import type { StockerFoupSlotState } from './stockerTypes';

export type StockerTrayProps = {
  slots: readonly StockerFoupSlotState[];
  trayW: number;
  trayD: number;
  trayH: number;
  slotH?: number;
  tierGap?: number;
  opacity?: number;
  detail?: AmhsDetailLevel;
};

/** Instanced silver rack trays with cyan lip LEDs. */
export const StockerTray: React.FC<StockerTrayProps> = ({
  slots,
  trayW,
  trayD,
  trayH,
  slotH,
  tierGap,
  opacity = 1,
  detail = 'high',
}) => {
  const mats = useMemo(() => getStockerMaterials(opacity), [opacity]);
  const deckInstances = useMemo(
    () => buildStockerTrayInstances(slots, trayW, trayD, trayH, slotH, tierGap),
    [slots, trayW, trayD, trayH, slotH, tierGap]
  );
  const cast = amhsCastShadow(detail);
  const receive = amhsReceiveShadow(detail);

  const ledInstances = useMemo(
    () =>
      slots.map((s) => ({
        position: [s.position[0] + trayW * 0.38, s.position[1] + trayH * 0.4, s.position[2] + trayD * 0.42] as [
          number,
          number,
          number,
        ],
        scale: [trayW * 0.12, trayH * 0.35, trayD * 0.04] as [number, number, number],
      })),
    [slots, trayW, trayD, trayH]
  );

  return (
    <group name="StockerTray">
      <AmhsInstancedBatch
        instances={deckInstances}
        material={mats.whiteMetal}
        castShadow={cast}
        receiveShadow={receive}
      />
      <AmhsInstancedBatch instances={ledInstances} material={mats.cyanLed} />
    </group>
  );
};

export default StockerTray;
